/**
 * Reusable field type select component.
 *
 * Fetches field types from the schema API, displays them with proper labels,
 * and supports additional unpersisted field types (e.g. for CSV import new
 * property creation).
 *
 * Uses the library Select component for visual consistency with all other
 * selects in the product.
 */

import { HelpCircle } from '@untitledui/icons';
import type { Key } from 'react';
import { useCallback, useMemo } from 'react';

import type { SelectSize } from '@/lib/base/forms/select/select';
import { Select } from '@/lib/base/forms/select/select';
import { SelectItem } from '@/lib/base/forms/select/select-item';
import { Tooltip, TooltipTrigger } from '@/lib/base/tooltip';
import { useFieldTypes } from '@/lib/crm/hooks/useFieldTypes';
import {
  getFieldTypeItems,
  getFieldTypeLabel,
} from '@/lib/crm/utils/fieldTypeHelpers';

/**
 * User-friendly descriptions for field types. The API descriptions are
 * written for LLM/developer consumption (format rules, JSON shapes,
 * rejection criteria); these are the plain-language equivalents shown
 * in tooltips.
 */
const USER_DESCRIPTIONS: Record<string, string> = {
  text: 'Single-line text for names, titles, or short descriptions.',
  email: 'An email address, e.g. jane@example.com.',
  url: 'A web address, e.g. https://example.com.',
  number: 'A numeric value, e.g. 42 or 3.14.',
  date: 'A calendar date, e.g. 2025-12-31.',
  select: 'A dropdown with predefined options to choose from.',
  timezone: 'A timezone identifier, e.g. America/New_York.',
  currency: 'A monetary amount with a currency.',
  phone_number: 'A phone number with country code.',
  web_source: 'A traffic source like Google Ads, Direct, or Organic Search.',
};

interface FieldTypeSelectProps {
  /** Currently selected field type name (e.g. 'text', 'number') */
  value: string;
  /** Called when the selection changes */
  onChange: (fieldTypeName: string) => void;
  /** Additional field type names to include beyond those from the API (for unpersisted types) */
  extraFieldTypes?: string[];
  /** Whether this is a new (unpersisted) property — shows "New · " prefix on the selected value */
  isNew?: boolean;
  /** Whether the select is disabled */
  disabled?: boolean;
  /** Tooltip shown when disabled to explain why (overrides the field type description). */
  disabledReason?: string;
  /** Accessible label */
  'aria-label'?: string;
  /** Size variant — defaults to 'xs'. */
  size?: SelectSize;
}

export function FieldTypeSelect({
  value,
  onChange,
  extraFieldTypes,
  isNew = false,
  disabled = false,
  disabledReason,
  'aria-label': ariaLabel = 'Field type',
  size = 'xs',
}: FieldTypeSelectProps) {
  const { data: fieldTypes = [] } = useFieldTypes();

  const selectItems = useMemo(() => {
    const apiItems = getFieldTypeItems(fieldTypes);
    const base =
      !extraFieldTypes || extraFieldTypes.length === 0
        ? apiItems
        : (() => {
            const existingIds = new Set(apiItems.map(i => i.id));
            const extras = extraFieldTypes
              .filter(ft => !existingIds.has(ft))
              .map(ft => ({
                id: ft,
                label: getFieldTypeLabel(ft, fieldTypes),
              }));
            return [...apiItems, ...extras];
          })();

    return base.map(item => ({
      id: item.id,
      label: isNew ? `New · ${item.label}` : item.label,
    }));
  }, [fieldTypes, extraFieldTypes, isNew]);

  const description = useMemo(() => {
    if (disabled && disabledReason) {
      return disabledReason;
    }
    if (USER_DESCRIPTIONS[value]) {
      return USER_DESCRIPTIONS[value];
    }
    const ft = fieldTypes.find(f => f.field_type_name === value);
    return ft?.description;
  }, [fieldTypes, value, disabled, disabledReason]);

  const handleChange = useCallback(
    (key: Key | null) => {
      if (key != null) {
        onChange(String(key));
      }
    },
    [onChange]
  );

  const selectedLabel = selectItems.find(i => i.id === value)?.label ?? value;

  // When disabled, render a static label with a tooltip instead of a
  // greyed-out select — makes it obvious the field type is fixed.
  if (disabled) {
    const reason = disabledReason ?? description;
    const label = (
      <span
        className='inline-flex items-center gap-1 text-xs text-fg-tertiary truncate'
        aria-label={ariaLabel}
      >
        {selectedLabel}
      </span>
    );

    return reason ? (
      <Tooltip title={reason} placement='top'>
        <TooltipTrigger className='cursor-default'>{label}</TooltipTrigger>
      </Tooltip>
    ) : (
      label
    );
  }

  return (
    <div className='flex items-center gap-1.5'>
      <Select
        value={value}
        onChange={handleChange}
        aria-label={ariaLabel}
        size={size}
        className='min-w-0 flex-1'
        items={selectItems}
      >
        {item => <SelectItem {...item} />}
      </Select>
      {description && (
        <Tooltip title={description} placement='top'>
          <TooltipTrigger
            aria-label={`${selectedLabel} info`}
            className='flex-shrink-0 text-fg-quaternary hover:text-fg-tertiary cursor-default'
          >
            <HelpCircle className='w-4 h-4' />
          </TooltipTrigger>
        </Tooltip>
      )}
    </div>
  );
}
